import React, { createContext, useContext, useId } from 'react';

// A Row names the control inside it: a Toggle in a Row is announced by the
// row's label (a screen reader used to hear only "switch" — Sandbox, 03.10.2026).
const RowLabel = createContext(null);

/**
 * Shared building blocks of the Settings modal pages (toggle switch, segmented
 * control, labelled row with separator, titled section). Extracted from
 * SettingsModal.js so extension settings pages (e.g. VaultExtensionSettings)
 * render with the exact same look as General / Appearance / etc.
 */

/**
 * `disabled` is for a setting this BUILD cannot honour — not for one that is
 * merely inapplicable right now. The vault's lock-on-suspend toggle is the
 * case it was added for: only Windows has the OS hooks behind it, and a
 * switch that reads "on" while nothing is listening is worse than no switch
 * at all. Pair it with a `desc` that says why; the control alone cannot.
 *
 * The real `disabled` attribute, not just a class: it takes the button out of
 * the tab order and stops the click, so the switch cannot be operated by
 * keyboard either. `aria-disabled` is set alongside it because some screen
 * readers skip `disabled` controls entirely, and here the point is to be
 * announced and explained rather than to disappear.
 */
export const Toggle = ({ value, onChange, disabled = false, label }) => {
  const rowLabelId = useContext(RowLabel);
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={label || undefined}
      aria-labelledby={label ? undefined : rowLabelId || undefined}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onChange(!value);
      }}
      className={`settings-toggle flex-shrink-0 ${value ? 'settings-toggle--on' : ''}${
        disabled ? ' settings-toggle--disabled' : ''
      }`}
    >
      <span className="settings-toggle-thumb" />
    </button>
  );
};

export const Segmented = ({ value, onChange, options, disabled = false }) => (
  <div className={`settings-segmented${disabled ? ' settings-control--disabled' : ''}`}>
    {options.map(({ id, label }) => (
      <button
        key={id}
        type="button"
        aria-pressed={value === id}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        onClick={() => {
          if (!disabled) onChange(id);
        }}
        className={`settings-segmented-btn${value === id ? ' settings-segmented-btn--active' : ''}`}
      >
        {label}
      </button>
    ))}
  </div>
);

/** A number field with its unit. `disabled` means the same as on `Toggle`. */
export const NumberInputWithUnit = ({ value, onChange, min, max, unit, width = 64, disabled = false }) => (
  <div className={`settings-input-group${disabled ? ' settings-control--disabled' : ''}`}>
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      disabled={disabled}
      onChange={(e) => {
        const n = parseInt(e.target.value, 10);
        if (!Number.isNaN(n)) onChange(n);
      }}
      className="settings-number-input"
      style={{ width }}
    />
    <span className="settings-unit-label">{unit}</span>
  </div>
);

export const Row = ({ label, desc, children }) => {
  const labelId = useId();
  return (
    <RowLabel.Provider value={labelId}>
      <div className="settings-row">
        {/* `settings-row-text` is a hook for pages whose descriptions are
            paragraphs rather than one-line hints — see `.mcp-settings` in
            McpSettings.css. `min-w-0` stays: it is what lets the column shrink
            inside the flex row instead of forcing it wider. */}
        <div className="settings-row-text min-w-0">
          <div className="settings-row-label" id={labelId}>{label}</div>
          {desc && <div className="settings-row-desc">{desc}</div>}
        </div>
        <div className="flex items-center flex-shrink-0 ml-4">{children}</div>
      </div>
    </RowLabel.Provider>
  );
};

export const Section = ({ title, children }) => (
  <div className="settings-section">
    <div className="settings-section-title">{title}</div>
    {children}
  </div>
);
